import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface ConversionState {
  cadastralAcres: string;
  squareMeters: string;
  hectares: string;
  squareFathoms: string;
  direction: 'toHectares' | 'fromHectares';
}

function KalkulatorKatastarskihJutara() {
  const [values, setValues] = useState<ConversionState>({
    cadastralAcres: '',
    squareMeters: '',
    hectares: '',
    squareFathoms: '',
    direction: 'toHectares'
  });

  // Conversion constants
  const CADASTRAL_ACRE_TO_SQUARE_METERS = 5754.64; // 1 katastarsko jutro = 5754.64 m²
  const SQUARE_FATHOM_TO_SQUARE_METERS = 3.5966; // 1 kvadratni hvat = 3.5966 m²
  const HECTARE_TO_SQUARE_METERS = 10000; // 1 hektar = 10000 m²

  const handleInputChange = (field: keyof ConversionState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));

    // Perform conversion based on the input field
    if (field === 'cadastralAcres') {
      convertFromCadastralAcres(value);
    } else if (field === 'hectares') {
      convertFromHectares(value);
    } else if (field === 'squareMeters') {
      convertFromSquareMeters(value);
    } else if (field === 'squareFathoms') {
      convertFromSquareFathoms(value);
    }
  };

  const convertFromCadastralAcres = (value: string) => {
    const acres = parseFloat(value);
    if (isNaN(acres)) {
      resetValues('cadastralAcres');
      return;
    }

    const squareMeters = acres * CADASTRAL_ACRE_TO_SQUARE_METERS;
    const hectares = squareMeters / HECTARE_TO_SQUARE_METERS;
    const squareFathoms = squareMeters / SQUARE_FATHOM_TO_SQUARE_METERS;

    setValues(prev => ({
      ...prev,
      squareMeters: squareMeters.toFixed(2),
      hectares: hectares.toFixed(4),
      squareFathoms: squareFathoms.toFixed(2)
    }));
  };

  const convertFromHectares = (value: string) => {
    const hectares = parseFloat(value);
    if (isNaN(hectares)) {
      resetValues('hectares');
      return;
    }

    const squareMeters = hectares * HECTARE_TO_SQUARE_METERS;
    const cadastralAcres = squareMeters / CADASTRAL_ACRE_TO_SQUARE_METERS;
    const squareFathoms = squareMeters / SQUARE_FATHOM_TO_SQUARE_METERS;

    setValues(prev => ({
      ...prev,
      squareMeters: squareMeters.toFixed(2),
      cadastralAcres: cadastralAcres.toFixed(4),
      squareFathoms: squareFathoms.toFixed(2)
    }));
  };

  const convertFromSquareMeters = (value: string) => {
    const squareMeters = parseFloat(value);
    if (isNaN(squareMeters)) {
      resetValues('squareMeters');
      return;
    }

    const hectares = squareMeters / HECTARE_TO_SQUARE_METERS;
    const cadastralAcres = squareMeters / CADASTRAL_ACRE_TO_SQUARE_METERS;
    const squareFathoms = squareMeters / SQUARE_FATHOM_TO_SQUARE_METERS;

    setValues(prev => ({
      ...prev,
      hectares: hectares.toFixed(4),
      cadastralAcres: cadastralAcres.toFixed(4),
      squareFathoms: squareFathoms.toFixed(2)
    }));
  };

  const convertFromSquareFathoms = (value: string) => {
    const squareFathoms = parseFloat(value);
    if (isNaN(squareFathoms)) {
      resetValues('squareFathoms');
      return;
    }

    const squareMeters = squareFathoms * SQUARE_FATHOM_TO_SQUARE_METERS;
    const hectares = squareMeters / HECTARE_TO_SQUARE_METERS;
    const cadastralAcres = squareMeters / CADASTRAL_ACRE_TO_SQUARE_METERS;

    setValues(prev => ({
      ...prev,
      squareMeters: squareMeters.toFixed(2),
      hectares: hectares.toFixed(4),
      cadastralAcres: cadastralAcres.toFixed(4)
    }));
  };

  const resetValues = (exceptField: keyof ConversionState) => {
    setValues(prev => ({
      ...prev,
      cadastralAcres: exceptField === 'cadastralAcres' ? prev.cadastralAcres : '',
      squareMeters: exceptField === 'squareMeters' ? prev.squareMeters : '',
      hectares: exceptField === 'hectares' ? prev.hectares : '',
      squareFathoms: exceptField === 'squareFathoms' ? prev.squareFathoms : ''
    }));
  };

  const handleClear = () => {
    setValues({
      cadastralAcres: '',
      squareMeters: '',
      hectares: '',
      squareFathoms: '',
      direction: 'toHectares'
    });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Katastarskih Jutara",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online kalkulator za pretvaranje katastarskih jutara u hektare, kvadratne metre i kvadratne hvate. Jednostavan za korištenje i precizan.",
    "url": "https://online-kalkulator.com/kalkulator-katastarskih-jutara",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Pretvaranje katastarskih jutara u hektare",
      "Pretvaranje hektara u katastarska jutra",
      "Pretvaranje u kvadratne metre",
      "Pretvaranje u kvadratne hvate",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Katastarskih Jutara | Pretvaranje u Hektare"
        description="Besplatni online kalkulator za pretvaranje katastarskih jutara u hektare, kvadratne metre i kvadratne hvate. Jednostavan za korištenje i precizan."
        canonicalUrl="/kalkulator-katastarskih-jutara"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Katastarskih Jutara</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Katastarska jutra
                  </label>
                  <input
                    type="number"
                    value={values.cadastralAcres}
                    onChange={(e) => handleInputChange('cadastralAcres', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite broj katastarskih jutara"
                    step="0.0001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hektari (ha)
                  </label>
                  <input
                    type="number"
                    value={values.hectares}
                    onChange={(e) => handleInputChange('hectares', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite broj hektara"
                    step="0.0001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kvadratni metri (m²)
                  </label>
                  <input
                    type="number"
                    value={values.squareMeters}
                    onChange={(e) => handleInputChange('squareMeters', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite broj kvadratnih metara"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kvadratni hvati (čhv)
                  </label>
                  <input
                    type="number"
                    value={values.squareFathoms}
                    onChange={(e) => handleInputChange('squareFathoms', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite broj kvadratnih hvati"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Clear Button */}
              <button
                onClick={handleClear}
                className="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Očisti sve
              </button>

              {/* Conversion Table */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Tablica pretvorbe:</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Jedinica
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ekvivalent
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1 katastarsko jutro</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">5754.64 m² = 0.5755 ha = 1600 čhv</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1 hektar (ha)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">10000 m² = 1.7377 kj = 2780.16 čhv</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1 kvadratni hvat (čhv)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">3.5966 m² = 0.00036 ha = 0.000625 kj</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Katastarskim Jutrima</h2>
                <p className="text-gray-600 leading-relaxed">
                  Katastarsko jutro je stara mjerna jedinica za površinu zemljišta koja se koristila u 
                  Austro-Ugarskoj Monarhiji, a kasnije i u zemljama koje su bile dio te monarhije, 
                  uključujući Hrvatsku, Bosnu i Hercegovinu, Srbiju i druge zemlje regije. Ova mjerna 
                  jedinica i danas se često koristi u katastarskim knjigama i neformalnim razgovorima 
                  o poljoprivrednom zemljištu.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Povijesni Kontekst</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Katastarsko jutro uvedeno je tijekom vladavine carice Marije Terezije u 18. stoljeću 
                  kao dio standardizacije mjernih jedinica u Habsburškoj Monarhiji. Jedno katastarsko 
                  jutro definirano je kao površina koju jedan par volova može preorati u jednom danu.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Ova mjerna jedinica bila je temelj za oporezivanje zemljišta i katastarsko mjerenje 
                  tijekom 19. i početkom 20. stoljeća. Iako su moderne mjerne jedinice poput hektara 
                  službeno zamijenile katastarska jutra, ova stara mjera i dalje se koristi u 
                  svakodnevnom govoru, posebno u ruralnim područjima.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Mjerne Jedinice i Pretvorbe</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Katastarsko jutro (kj)</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>1 katastarsko jutro = 5754.64 kvadratnih metara</li>
                      <li>1 katastarsko jutro = 0.5755 hektara</li>
                      <li>1 katastarsko jutro = 1600 kvadratnih hvati</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Hektar (ha)</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>1 hektar = 10000 kvadratnih metara</li>
                      <li>1 hektar = 1.7377 katastarskih jutara</li>
                      <li>1 hektar = 2780.16 kvadratnih hvati</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Kvadratni hvat (čhv)</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>1 kvadratni hvat = 3.5966 kvadratnih metara</li>
                      <li>1 kvadratni hvat = 0.00036 hektara</li>
                      <li>1 kvadratni hvat = 0.000625 katastarskih jutara</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti Kalkulator</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite vrijednost u bilo koje polje za unos</li>
                  <li>Kalkulator će automatski izračunati ekvivalentne vrijednosti u drugim jedinicama</li>
                  <li>Za preciznije izračune, koristite decimalne brojeve</li>
                  <li>Kliknite "Očisti sve" za resetiranje svih polja</li>
                  <li>Koristite tablicu pretvorbe za brzi pregled odnosa između jedinica</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjene u Praksi</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Čitanje starih katastarskih planova i zemljišnih knjiga</li>
                  <li>Kupoprodaja poljoprivrednog zemljišta u regijama gdje se još koriste stare mjere</li>
                  <li>Razumijevanje povijesnih dokumenata o zemljištu</li>
                  <li>Konverzija između različitih mjernih sustava za površinu zemljišta</li>
                  <li>Procjena vrijednosti zemljišta na temelju površine</li>
                  <li>Planiranje poljoprivredne proizvodnje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Regionalne Varijacije</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Važno je napomenuti da su postojale regionalne varijacije u definiciji katastarskog 
                  jutra i kvadratnog hvata. Ovaj kalkulator koristi standardne vrijednosti koje su 
                  najčešće korištene u Hrvatskoj i okolnim zemljama.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Austrijsko/hrvatsko katastarsko jutro = 5754.64 m²</li>
                  <li>Mađarsko katastarsko jutro (hold) = 5755.41 m²</li>
                  <li>Bečki kvadratni hvat = 3.5966 m²</li>
                  <li>Mađarski kvadratni hvat = 3.5967 m²</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Svi izračuni su zaokruženi na odgovarajući broj decimala</li>
                  <li>Za službene svrhe uvijek koristite moderne mjerne jedinice (hektare i kvadratne metre)</li>
                  <li>U zemljišnim knjigama i katastarskim planovima često se još uvijek mogu naći stare mjerne jedinice</li>
                  <li>Kod kupoprodaje zemljišta, uvijek provjerite točnu površinu u službenim dokumentima</li>
                  <li>Različiti izvori mogu koristiti blago različite vrijednosti za pretvorbu</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorKatastarskihJutara;